import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import {
  Shield, Users, Bell, Check, X, RefreshCw, MessageSquare, Bug,
  Lightbulb, HelpCircle, MessageCircle, ChevronDown, ChevronUp, Send, LogOut,
  Home, Trash2, CheckCheck, Archive
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const CATEGORY_ICON = {
  bug: <Bug className="w-4 h-4 text-red-400" />,
  suggestion: <Lightbulb className="w-4 h-4 text-yellow-400" />,
  general: <MessageCircle className="w-4 h-4 text-blue-400" />,
  question: <HelpCircle className="w-4 h-4 text-purple-400" />,
};

const STATUS_BADGE = {
  pending: <Badge className="bg-yellow-600 text-white">Pending</Badge>,
  approved: <Badge className="bg-green-600 text-white">Approved</Badge>,
  rejected: <Badge className="bg-red-700 text-white">Rejected</Badge>,
};

const FEEDBACK_STATUS_BADGE = {
  new: <Badge className="bg-blue-600 text-white">New</Badge>,
  reviewed: <Badge className="bg-yellow-600 text-white">Reviewed</Badge>,
  resolved: <Badge className="bg-green-600 text-white">Resolved</Badge>,
};

const DISCIPLINE_BADGE = {
  cnc_routing: <Badge className="bg-orange-800/60 text-orange-300 text-xs">CNC Router</Badge>,
  '3d_printing': <Badge className="bg-blue-800/60 text-blue-300 text-xs">3D Printing</Badge>,
  laser_cutting: <Badge className="bg-red-800/60 text-red-300 text-xs">Laser</Badge>,
};

// Applicant card discipline badges — unified orange style per spec
const APPLICANT_DISC_BADGE = {
  cnc_routing: <Badge className="bg-[#c85a00] text-white text-xs">CNC Router</Badge>,
  '3d_printing': <Badge className="bg-[#c85a00] text-white text-xs">3D Printing</Badge>,
  laser_cutting: <Badge className="bg-[#c85a00] text-white text-xs">Laser Cutting</Badge>,
};

export default function AdminDashboard() {
  const { token, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [testersWithFeedback, setTestersWithFeedback] = useState([]);
  const [featureNotifications, setFeatureNotifications] = useState([]);
  const [allFeedback, setAllFeedback] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Applicant discipline filter
  const [disciplineFilter, setDisciplineFilter] = useState('all');

  // Delete user confirmation
  const [userToDelete, setUserToDelete] = useState(null);

  // Feedback response state
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [markResolved, setMarkResolved] = useState(true);
  const [submittingResponse, setSubmittingResponse] = useState(false);

  // Feedback filters
  const [feedbackModuleFilter, setFeedbackModuleFilter] = useState('all');
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [markingResolved, setMarkingResolved] = useState(null);

  useEffect(() => {
    verifyAdminAccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAdminAccess = async () => {
    try {
      const res = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.is_admin) {
        setIsAdmin(true);
        await loadAllData();
      }
    } catch {
      setIsAdmin(false);
      toast.error('Admin access denied');
      setTimeout(() => navigate('/dashboard'), 1500);
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    setRefreshing(true);
    try {
      const [statsRes, testersRes, notificationsRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/testers-with-feedback`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/feature-notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/beta-feedback`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setStats(statsRes.data);

      const normalized = testersRes.data.map(t => ({
        ...t,
        id: t._id || t.id,
        feedback: (t.feedback || []).map(f => ({ id: f._id || f.id, ...f }))
      }));
      setTestersWithFeedback(normalized);
      setFeatureNotifications(notificationsRes.data);
      setAllFeedback(feedbackRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load admin data');
    } finally {
      setRefreshing(false);
    }
  };

  const updateApplicationStatus = async (appId, newStatus) => {
    try {
      await axios.post(
        `${API}/admin/tester-applications/${appId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Application ${newStatus} — notification email sent`);
      await loadAllData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${userToDelete.email} permanently deleted`);
      setUserToDelete(null);
      await loadAllData();
    } catch {
      toast.error('Failed to delete user');
      setUserToDelete(null);
    }
  };

  const submitFeedbackResponse = async (feedbackId) => {
    if (!responseText.trim()) {
      toast.error('Response cannot be empty');
      return;
    }
    setSubmittingResponse(true);
    try {
      await axios.post(
        `${API}/admin/beta-feedback/${feedbackId}/respond`,
        { response: responseText.trim(), mark_resolved: markResolved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Response sent');
      setRespondingTo(null);
      setResponseText('');
      setMarkResolved(true);
      await loadAllData();
    } catch {
      toast.error('Failed to send response');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const filteredTesters = disciplineFilter === 'all'
    ? testersWithFeedback
    : testersWithFeedback.filter(t => t.discipline === disciplineFilter);

  const markAsResolved = async (feedbackId) => {
    setMarkingResolved(feedbackId);
    try {
      await axios.post(
        `${API}/admin/beta-feedback/${feedbackId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Marked as resolved');
      await loadAllData();
    } catch {
      toast.error('Failed to mark as resolved');
    } finally {
      setMarkingResolved(null);
    }
  };

  const moduleFeedback = feedbackModuleFilter === 'all'
    ? allFeedback
    : allFeedback.filter(f => f.discipline === feedbackModuleFilter);

  const openFeedback = moduleFeedback.filter(f => f.status !== 'resolved');
  const resolvedFeedback = moduleFeedback.filter(f => f.status === 'resolved');

  const visibleFeedback = feedbackStatusFilter === 'resolved'
    ? resolvedFeedback
    : feedbackStatusFilter === 'open'
      ? openFeedback
      : openFeedback;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-orange-500" />
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
          <Button onClick={loadAllData} disabled={refreshing} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="ghost" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Applicants', value: stats.total_test_applications },
            { label: 'Pending', value: stats.pending_test_applications },
            { label: 'Approved', value: stats.approved_test_applications },
            { label: 'Feedback Items', value: stats.total_feedback },
          ].map(s => (
            <Card key={s.label} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-orange-400">{s.value ?? '—'}</div>
                <div className="text-xs text-zinc-400 mt-1">{s.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="applicants">
        <TabsList className="bg-zinc-900 border border-zinc-800 mb-4">
          <TabsTrigger value="applicants">
            <Users className="w-4 h-4 mr-2" />
            Applicants
          </TabsTrigger>
          <TabsTrigger value="feedback">
            <MessageSquare className="w-4 h-4 mr-2" />
            Feedback
            {allFeedback.filter(f => f.status === 'new').length > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2">
                {allFeedback.filter(f => f.status === 'new').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notify-Me
          </TabsTrigger>
        </TabsList>

        {/* ===================== APPLICANTS TAB ===================== */}
        <TabsContent value="applicants">
          {/* Discipline filter with counts */}
          {(() => {
            const counts = {
              all: testersWithFeedback.length,
              cnc_routing: testersWithFeedback.filter(t => t.discipline === 'cnc_routing').length,
              '3d_printing': testersWithFeedback.filter(t => t.discipline === '3d_printing').length,
              laser_cutting: testersWithFeedback.filter(t => t.discipline === 'laser_cutting').length,
            };
            const labels = { all: 'All', cnc_routing: 'CNC', '3d_printing': '3D Printing', laser_cutting: 'Laser' };
            return (
              <div className="flex flex-wrap gap-2 mb-4">
                {['all', 'cnc_routing', '3d_printing', 'laser_cutting'].map(d => (
                  <Button
                    key={d}
                    size="sm"
                    variant={disciplineFilter === d ? 'default' : 'outline'}
                    onClick={() => setDisciplineFilter(d)}
                  >
                    {labels[d]} ({counts[d]})
                  </Button>
                ))}
              </div>
            );
          })()}

          {filteredTesters.length === 0 ? (
            <p className="text-zinc-500 text-sm">No applicants found.</p>
          ) : (
            <div className="space-y-3">
              {filteredTesters.map(tester => (
                <Card key={tester.id} className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-white font-medium">
                            {tester.first_name} {tester.surname}
                          </div>
                          {tester.existing_user_id && (
                            <Badge className="bg-zinc-700 text-zinc-300 text-xs">Existing User</Badge>
                          )}
                        </div>
                        <div className="text-zinc-400 text-sm">{tester.email}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {APPLICANT_DISC_BADGE[tester.discipline]}
                          <span className="text-zinc-500 text-xs">{tester.country}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {STATUS_BADGE[tester.status] || <Badge>{tester.status}</Badge>}
                        {tester.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-500 h-8"
                              onClick={() => updateApplicationStatus(tester.id, 'approved')}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-500 hover:bg-red-900/20 h-8"
                              onClick={() => updateApplicationStatus(tester.id, 'rejected')}
                            >
                              <X className="w-3 h-3 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-800 text-red-500 hover:bg-red-950/40 h-8"
                          onClick={() => setUserToDelete(tester)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete User
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===================== FEEDBACK TAB ===================== */}
        <TabsContent value="feedback">
          {/* Module filter */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {['all', 'cnc_routing', '3d_printing', 'laser_cutting'].map(d => (
              <Button
                key={d}
                size="sm"
                variant={feedbackModuleFilter === d ? 'default' : 'outline'}
                onClick={() => setFeedbackModuleFilter(d)}
              >
                {d === 'all' ? 'All' : d === 'cnc_routing' ? 'CNC Router' : d === '3d_printing' ? '3D Printing' : 'Laser'}
              </Button>
            ))}
          </div>

          {/* Status filter */}
          <div className="flex gap-2 mb-4">
            {['all', 'open', 'resolved'].map(s => (
              <Button
                key={s}
                size="sm"
                variant={feedbackStatusFilter === s ? 'default' : 'outline'}
                onClick={() => setFeedbackStatusFilter(s)}
              >
                {s === 'all' ? 'All' : s === 'open' ? 'Open' : 'Resolved'}
              </Button>
            ))}
          </div>

          {moduleFeedback.length === 0 ? (
            <p className="text-zinc-500 text-sm">No feedback submitted yet.</p>
          ) : (
            <>
              {/* Main list: open (or resolved if filter=resolved) */}
              {visibleFeedback.length === 0 ? (
                <p className="text-zinc-500 text-sm mb-4">No {feedbackStatusFilter === 'open' ? 'open' : ''} feedback.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {visibleFeedback.map(fb => (
                <Card key={fb.id || fb._id} className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-4">
                    {/* Row 1: icon + subject + status + toggle */}
                    <div
                      className="flex justify-between items-start cursor-pointer"
                      onClick={() =>
                        setExpandedFeedback(
                          expandedFeedback === (fb.id || fb._id) ? null : (fb.id || fb._id)
                        )
                      }
                    >
                      <div className="flex items-start gap-2">
                        {CATEGORY_ICON[fb.category] || <MessageCircle className="w-4 h-4 text-zinc-400" />}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-white font-medium">{fb.subject}</div>
                            {fb.discipline && DISCIPLINE_BADGE[fb.discipline]}
                          </div>
                          <div className="text-zinc-400 text-xs">
                            {fb.user_name} · {fb.user_email} · {new Date(fb.submitted_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {FEEDBACK_STATUS_BADGE[fb.status] || <Badge>{fb.status}</Badge>}
                        {expandedFeedback === (fb.id || fb._id)
                          ? <ChevronUp className="w-4 h-4 text-zinc-400" />
                          : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                      </div>
                    </div>

                    {/* Expanded section */}
                    {expandedFeedback === (fb.id || fb._id) && (
                      <div className="mt-4 space-y-3 border-t border-zinc-800 pt-3">
                        {/* Original message */}
                        <div>
                          <div className="text-xs text-zinc-500 font-semibold mb-1">USER MESSAGE</div>
                          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{fb.message}</p>
                        </div>

                        {/* Existing admin response */}
                        {fb.admin_response?.response_text && (
                          <div className="bg-orange-950/30 border border-orange-800/40 rounded p-3">
                            <div className="text-xs text-orange-400 font-semibold mb-1">
                              YOUR REPLY · {fb.admin_response.responded_at
                                ? new Date(fb.admin_response.responded_at).toLocaleDateString()
                                : ''}
                            </div>
                            <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                              {fb.admin_response.response_text}
                            </p>
                          </div>
                        )}

                        {/* Reply form */}
                        {respondingTo === (fb.id || fb._id) ? (
                          <div className="space-y-3">
                            <Textarea
                              placeholder="Write your response to the user..."
                              value={responseText}
                              onChange={e => setResponseText(e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                            />
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`resolve-${fb.id}`}
                                checked={markResolved}
                                onCheckedChange={setMarkResolved}
                              />
                              <Label htmlFor={`resolve-${fb.id}`} className="text-zinc-300 text-sm">
                                Mark as resolved
                              </Label>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-orange-600 hover:bg-orange-500"
                                disabled={submittingResponse}
                                onClick={() => submitFeedbackResponse(fb.id || fb._id)}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                {submittingResponse ? 'Sending...' : 'Send Reply'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setRespondingTo(null); setResponseText(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-700 text-orange-400 hover:bg-orange-900/20"
                              onClick={() => {
                                setRespondingTo(fb.id || fb._id);
                                setResponseText(fb.admin_response?.response_text || '');
                              }}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              {fb.admin_response?.response_text ? 'Edit Reply' : 'Reply'}
                            </Button>
                            {fb.status !== 'resolved' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-700 text-green-400 hover:bg-green-900/20"
                                disabled={markingResolved === (fb.id || fb._id)}
                                onClick={() => markAsResolved(fb.id || fb._id)}
                              >
                                <CheckCheck className="w-3 h-3 mr-1" />
                                {markingResolved === (fb.id || fb._id) ? 'Resolving...' : 'Mark Resolved'}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                  ))}
                </div>
              )}

              {/* Collapsible Archived / Resolved section — visible when filter is all or open */}
              {feedbackStatusFilter !== 'resolved' && resolvedFeedback.length > 0 && (
                <div className="mt-4">
                  <button
                    className="flex items-center gap-2 text-zinc-400 hover:text-zinc-200 text-sm mb-3 transition-colors"
                    onClick={() => setShowArchived(v => !v)}
                  >
                    <Archive className="w-4 h-4" />
                    <span>Archived / Resolved ({resolvedFeedback.length})</span>
                    {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showArchived && (
                    <div className="space-y-3 opacity-70">
                      {resolvedFeedback.map(fb => (
                        <Card key={fb.id || fb._id} className="bg-zinc-900/50 border-zinc-800/50">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2 flex-wrap">
                                {CATEGORY_ICON[fb.category] || <MessageCircle className="w-4 h-4 text-zinc-500" />}
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-zinc-300 font-medium text-sm">{fb.subject}</span>
                                    {fb.discipline && DISCIPLINE_BADGE[fb.discipline]}
                                  </div>
                                  <div className="text-zinc-500 text-xs">
                                    {fb.user_name} · {new Date(fb.submitted_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <Badge className="bg-green-600 text-white text-xs">Resolved</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ===================== NOTIFY-ME TAB ===================== */}
        <TabsContent value="notifications">
          {featureNotifications.length === 0 ? (
            <p className="text-zinc-500 text-sm">No feature notification signups yet.</p>
          ) : (
            <div className="space-y-2">
              {featureNotifications.map(n => (
                <Card key={n.id || n._id} className="bg-zinc-900 border-zinc-800">
                  <CardContent className="flex justify-between items-center p-4">
                    <div>
                      <div className="text-white text-sm font-medium">{n.email}</div>
                      {n.name && <div className="text-zinc-400 text-xs">{n.name}</div>}
                    </div>
                    <Badge className="bg-zinc-700 text-zinc-200 capitalize">
                      {n.feature?.replace('_', ' ')}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => { if (!open) setUserToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong className="text-white">{userToDelete?.first_name} {userToDelete?.surname}</strong> ({userToDelete?.email})?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 hover:bg-red-600 text-white"
              onClick={handleDeleteUser}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
